/**
 * Stack Component
 *
 * A layout primitive for vertical stacking with consistent spacing.
 * Uses the Tailwind spacing scale for gap values.
 *
 * Features:
 * - Vertical flex layout with gap-based spacing
 * - Named spacing scale (xs, sm, md, lg, xl, 2xl)
 * - Alignment and justification options
 * - Responsive gap support
 * - No arbitrary values - all gaps from Tailwind scale
 *
 * @example Basic usage
 * ```tsx
 * <Stack gap="md">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 * </Stack>
 * ```
 *
 * @example With alignment
 * ```tsx
 * <Stack gap="lg" align="center">
 *   <Heading>Centered content</Heading>
 *   <Text>Description text</Text>
 * </Stack>
 * ```
 *
 * @example Numeric gap values
 * ```tsx
 * <Stack gap={4}>Gap of 1rem (16px)</Stack>
 * <Stack gap={8}>Gap of 2rem (32px)</Stack>
 * ```
 */

import * as React from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/utils';
import { stackVariants } from './Stack.variants';

// =============================================================================
// TYPES
// =============================================================================

interface StackProps
  extends React.HTMLAttributes<HTMLElement>,
  VariantProps<typeof stackVariants> {
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?:
  | "div"
  | "section"
  | "article"
  | "main"
  | "aside"
  | "nav"
  | "header"
  | "footer";
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Stack - Vertical layout primitive
 *
 * Use Stack for any vertical arrangement of elements with consistent spacing.
 * Pages and features MUST use this instead of ad-hoc flex containers.
 *
 * Replaces patterns like:
 * - `className="flex flex-col gap-4"`
 * - `className="space-y-6"`
 * - `className="flex flex-col items-center gap-8"`
 */
const Stack = React.memo(
  React.forwardRef<HTMLDivElement, StackProps>(
    (
      {
        as: Component = 'div',
        gap,
        padding,
        align,
        justify,
        fullWidth,
        fullHeight,
        className,
        children,
        ...props
      },
      ref
    ) => {
      return (
        <Component
          ref={ref as React.Ref<never>}
          className={cn(stackVariants({ gap, padding, align, justify, fullWidth, fullHeight }), className)}
          data-layout="stack"
          {...props}
        >
          {children}
        </Component>
      );
    }
  )
);

Stack.displayName = 'Stack';

// =============================================================================
// EXPORTS
// =============================================================================

export { Stack };
